import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { XCircle, ArrowLeft, Calendar, CreditCard } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const PaymentCancelled = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const [retrying, setRetrying] = useState(false);

  const handleRetryPayment = async () => {
    if (!bookingId) {
      toast.error('Booking ID not found');
      return;
    }

    try {
      setRetrying(true);
      // Get the booking to fetch checkout URL or create new one
      const response = await api.get(`/bookings/${bookingId}`);
      const booking = response.data;

      if (booking.stripeCheckoutSessionId) {
        // Try to get the checkout URL
        const checkoutResponse = await api.get(`/payments/checkout/session/${booking.stripeCheckoutSessionId}`);
        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
          return;
        }
      }

      toast.error('Unable to retry payment. Please contact support.');
    } catch (error) {
      console.error('Retry payment error:', error);
      toast.error('Failed to retry payment');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6">
          <div className="text-center space-y-6">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center">
                <XCircle className="w-12 h-12 text-yellow-600" />
              </div>
            </div>

            {/* Title */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Cancelled
              </h1>
              <p className="text-gray-600">
                Your payment was cancelled. No charges were made.
              </p>
            </div>

            {/* Booking Info */}
            {bookingId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Booking ID:</span> {bookingId.slice(0, 8)}...
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  The booking has been created but is awaiting payment.
                  You can retry payment now or from the Bookings page.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 pt-4">
              <Button
                onClick={handleRetryPayment}
                disabled={retrying}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {retrying ? 'Loading...' : 'Retry Payment'}
              </Button>
              <Button
                onClick={() => navigate('/dashboard/bookings')}
                variant="outline"
                className="w-full"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Go to Bookings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentCancelled;
